// Contenu de l'onglet Profils de rôle - Design moderne avec Cards

import React, { useState } from 'react';
import {
  Alert,
  Card,
  Button,
  Space,
  Tag,
  Popconfirm,
  message,
  Input,
  Badge,
  Empty,
  Spin,
  Row,
  Col,
  Typography,
  Dropdown,
  Menu,
  Modal,
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CopyOutlined, 
  SearchOutlined,
  SafetyOutlined,
  UserOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { useRoleProfilesByBusinessId, useDeleteRoleProfile, useDuplicateRoleProfile } from '../../../hooks/useRoleProfiles';
import { useBusiness } from '../../../hooks/useBusinesses';
import { RoleProfile } from '../../../types/user';
import RoleProfileForm from '../../../features/role-profiles/components/RoleProfileForm';
import { matchesSearchInFields } from '../../../utils/searchUtils';
import {
  canCreateRoleProfiles,
  getRoleProfilesFeatureGateMessage,
} from '../../../utils/roleProfileFeatureSync';
import './RoleProfilesTabContent.css';

const { Search } = Input;
const { Text } = Typography;

interface RoleProfilesTabContentProps {
  businessId: string;
  businessName?: string;
}

const RoleProfilesTabContent: React.FC<RoleProfilesTabContentProps> = ({
  businessId,
  businessName,
}) => {
  const [formVisible, setFormVisible] = useState(false);
  const [editingProfile, setEditingProfile] = useState<RoleProfile | null>(null);
  const [searchText, setSearchText] = useState('');

  const { data: business } = useBusiness(businessId);
  const { data: roleProfiles = [], isLoading } = useRoleProfilesByBusinessId(businessId);
  const deleteMutation = useDeleteRoleProfile();
  const duplicateMutation = useDuplicateRoleProfile();
  const canCreate = canCreateRoleProfiles(business ?? null);

  const filteredProfiles = roleProfiles.filter((profile) =>
    matchesSearchInFields([profile.name, profile.description], searchText)
  );

  const handleCreate = () => {
    if (!canCreate) {
      message.warning(getRoleProfilesFeatureGateMessage(business ?? null));
      return;
    }
    setEditingProfile(null);
    setFormVisible(true);
  };

  const handleEdit = (profile: RoleProfile) => {
    setEditingProfile(profile);
    setFormVisible(true);
  };

  const handleDelete = async (profileId: string) => {
    try {
      const profile = roleProfiles.find((p) => p.id === profileId);
      if (profile && profile.usageCount && profile.usageCount > 0) {
        message.error(
          `Ce profil est utilisé par ${profile.usageCount} utilisateur(s). Impossible de le supprimer.`
        );
        return;
      }
      await deleteMutation.mutateAsync(profileId);
      message.success('Profil supprimé avec succès');
    } catch (error: any) {
      message.error(error.message || 'Erreur lors de la suppression');
    }
  };

  const handleDuplicate = async (profileId: string) => {
    if (!canCreate) {
      message.warning(getRoleProfilesFeatureGateMessage(business ?? null));
      return;
    }
    try {
      await duplicateMutation.mutateAsync(profileId);
      message.success('Profil dupliqué avec succès');
    } catch (error: any) {
      message.error(error.message || 'Erreur lors de la duplication');
    }
  };

  const handleFormSuccess = () => {
    setFormVisible(false);
    setEditingProfile(null);
    message.success(editingProfile ? 'Profil modifié avec succès' : 'Profil créé avec succès');
  };

  const handleFormCancel = () => {
    setFormVisible(false);
    setEditingProfile(null);
  };

  // Rendu d'une card de profil de rôle
  const renderRoleProfileCard = (profile: RoleProfile) => {
    const menu = (
      <Menu>
        <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEdit(profile)}>
          Modifier
        </Menu.Item>
        <Menu.Item
          key="duplicate"
          icon={<CopyOutlined />}
          disabled={!canCreate}
          onClick={() => handleDuplicate(profile.id)}
        >
          Dupliquer
        </Menu.Item>
        {!profile.isDefault && (profile.usageCount || 0) === 0 && (
          <>
            <Menu.Divider />
            <Menu.Item 
              key="delete" 
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Supprimer ce profil ?',
                  content: 'Cette action est irréversible.',
                  okText: 'Supprimer',
                  okType: 'danger',
                  cancelText: 'Annuler',
                  onOk: () => handleDelete(profile.id),
                });
              }}
            >
              Supprimer
            </Menu.Item>
          </>
        )}
      </Menu>
    );

    const permissionsCount = profile.permissions ? Object.values(profile.permissions).filter(Boolean).length : 0;

    return (
      <Card 
        key={profile.id}
        className="role-profile-card"
        hoverable
        style={{
          marginBottom: '16px',
          borderLeft: `4px solid ${profile.isActive ? '#52c41a' : '#d9d9d9'}`,
        }}
      >
        <Row gutter={16} align="middle">
          {/* Infos principales */}
          <Col xs={24} sm={24} md={16}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {/* En-tête */}
              <div>
                <Space wrap size={[8, 4]}>
                  <Space size={8}>
                    <SafetyOutlined style={{ fontSize: '18px', color: '#722ed1' }} />
                    <Text strong style={{ fontSize: '16px' }}>{profile.name}</Text>
                  </Space>
                  {profile.isDefault && (
                    <Tag color="gold" icon={<CrownOutlined />}>Par défaut</Tag>
                  )}
                  <Tag color={profile.isActive ? 'green' : 'red'} icon={profile.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                    {profile.isActive ? 'Actif' : 'Inactif'}
                  </Tag>
                </Space>
              </div>

              {/* Description */}
              {profile.description && (
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  {profile.description}
                </Text>
              )}

              {/* Statistiques */}
              <Space size={16}>
                <Space size={4}>
                  <UserOutlined style={{ color: '#8c8c8c' }} />
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    {profile.usageCount || 0} utilisateur{(profile.usageCount || 0) > 1 ? 's' : ''}
                  </Text>
                </Space>
                <Space size={4}>
                  <SafetyOutlined style={{ color: '#8c8c8c' }} />
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    {permissionsCount} permission{permissionsCount > 1 ? 's' : ''}
                  </Text>
                </Space>
              </Space>
            </Space>
          </Col>

          {/* Actions */}
          <Col xs={24} sm={24} md={8}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '12px'
            }}>
              <Space size={8}>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(profile)}
                  style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                >
                  Modifier
                </Button>
                <Dropdown overlay={menu} trigger={['click']}>
                  <Button icon={<MoreOutlined />} />
                </Dropdown>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div className="role-profiles-tab-content">
      {/* En-tête */}
      <Card className="header-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <Text strong style={{ fontSize: '16px', display: 'block' }}>Profils de rôle</Text>
            {businessName && (
              <Text type="secondary" style={{ fontSize: '13px' }}>
                Créez et gérez les profils de permissions pour {businessName}
              </Text>
            )}
          </div>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            disabled={!canCreate}
            title={!canCreate ? getRoleProfilesFeatureGateMessage(business ?? null) : undefined}
          >
            Créer un profil
          </Button>
        </div>
      </Card>

      {!canCreate && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={getRoleProfilesFeatureGateMessage(business ?? null)}
        />
      )}

      {/* Recherche */}
      <Card style={{ marginBottom: 16 }} className="search-card">
        <Search
          placeholder="Rechercher un profil (nom, description)..."
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          prefix={<SearchOutlined />}
          size="large"
        />
      </Card>

      {/* Compteur */}
      <div style={{ marginBottom: '16px' }}>
        <Text type="secondary">
          {filteredProfiles.length} profil{filteredProfiles.length > 1 ? 's' : ''} trouvé{filteredProfiles.length > 1 ? 's' : ''}
        </Text>
      </div>

      {/* Liste des profils */}
      <Spin spinning={isLoading} size="large">
        {filteredProfiles.length === 0 ? (
          <Card>
            <Empty 
              description={searchText ? "Aucun profil trouvé" : "Aucun profil créé"}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              {!searchText && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                  disabled={!canCreate}
                >
                  Créer votre premier profil
                </Button>
              )}
            </Empty>
          </Card>
        ) : (
          <div className="role-profiles-list">
            {filteredProfiles.map(profile => renderRoleProfileCard(profile))}
          </div>
        )}
      </Spin>

      <RoleProfileForm
        visible={formVisible}
        profile={editingProfile}
        businessId={businessId}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
      />
    </div>
  );
};

export default RoleProfilesTabContent;
